/**
 * Ticket Action Modals
 * 
 * Specific modal designs for each ticket type based on the CSV configuration.
 * Each ticket type has its own modal with required fields and actions.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FiX, FiCheck, FiAlertCircle, FiPackage, FiClock, FiMapPin,
  FiDollarSign, FiUser, FiTruck, FiMessageSquare, FiExternalLink,
  FiPhone, FiMail, FiCamera, FiRefreshCw, FiChevronDown, FiChevronRight,
  FiSend, FiCalendar, FiEdit3, FiAlertTriangle, FiLink, FiSlack,
  FiUsers, FiSearch, FiCheckCircle, FiXCircle, FiInfo, FiShoppingCart,
  FiHome, FiClipboard, FiImage
} from 'react-icons/fi';
import OrderContextPanel, { formatToPST, formatDatePST, formatTimePST } from './OrderContextPanel';
import request from '../../utils/request';
import { formatEntryInstructionsLastUpdated, resolveEntryInstructionsLastUpdated } from '../../utils/formatDate';

// ═══════════════════════════════════════════════════════════════════════════
// MAIN TICKET ACTION MODAL ROUTER
// ═══════════════════════════════════════════════════════════════════════════
export default function TicketActionModal({
  ticket,
  order,
  onClose,
  onComplete,
  onSaveData,
  onEscalate,
}) {
  if (!ticket) return null;

  const modalProps = { ticket, order, onClose, onComplete, onSaveData, onEscalate };

  // Route to appropriate modal based on ticket type
  switch (ticket.ticketTypeId || ticket.type) {
    case 'PLACE_ORDER':
      return <PlaceOrderModal {...modalProps} />;
    case 'GET_DELIVERY_TIME':
      return <GetDeliveryTimeModal {...modalProps} />;
    case 'VERIFY_ENTRY':
      return <VerifyEntryModal {...modalProps} />;
    case 'SET_DRIVER_PAY':
      return <SetDriverPayModal {...modalProps} />;
    case 'FIND_DRIVER':
      return <FindDriverModal {...modalProps} />;
    case 'MONITOR_DRIVER':
      return <MonitorDriverModal {...modalProps} />;
    case 'DRIVER_SUPPORT':
      return <DriverSupportModal {...modalProps} />;
    case 'REFUND_APPROVAL':
      return <RefundApprovalModal {...modalProps} />;
    case 'POST_JOB_EXTERNAL':
      return <PostJobExternalModal {...modalProps} />;
    case 'MONITOR_EXTERNAL_DRIVER':
      return <MonitorExternalDriverModal {...modalProps} />;
    case 'INQUIRY_SUPPORT':
      return <InquirySupportModal {...modalProps} />;
    case 'RESCHEDULE_ORDER':
      return <RescheduleOrderModal {...modalProps} />;
    case 'CANCEL_ORDER':
      return <CancelOrderModal {...modalProps} />;
    case 'SUBSTITUTION_REVIEW':
      return <SubstitutionReviewModal {...modalProps} />;
    case 'QUALITY_VERIFICATION':
      return <QualityVerificationModal {...modalProps} />;
    case 'REVIEW_DELIVERY_ITEMS':
      return <ReviewDeliveryItemsModal {...modalProps} />;
    case 'VENDOR_REFUND':
      return <VendorRefundModal {...modalProps} />;
    case 'GUEST_SUPPORT':
      return <GuestSupportModal {...modalProps} />;
    default:
      return <GenericTicketModal {...modalProps} />;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// MODAL WRAPPER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
function ModalWrapper({ title, subtitle, ticket, order, onClose, children, footer, showOrderContext = true }) {
  // Show context by default if order data is available
  const [showContext, setShowContext] = useState(!!order);
  
  const priorityColor = {
    P0: 'bg-red-600 text-white',
    P1: 'bg-amber-500 text-white',
    P2: 'bg-gray-500 text-white',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900">
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${priorityColor[ticket?.priority] || priorityColor.P2}`}>
              {ticket?.priority || 'P2'}
            </span>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">{title}</h2>
              {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {showOrderContext && order && (
              <button
                onClick={() => setShowContext(!showContext)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                  showContext 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
                }`}
              >
                {showContext ? 'Hide' : 'Show'} Order Context
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 hover:text-slate-700 dark:hover:text-white">
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content - Split view when context is shown */}
        <div className="flex-1 overflow-hidden flex">
          {/* Main Content */}
          <div className={`flex-1 overflow-y-auto p-6 ${showContext && order ? 'border-r border-slate-200 dark:border-slate-700' : ''}`}>
            {children}
          </div>
          
          {/* Order Context Panel */}
          {showContext && order && (
            <div className="w-[380px] overflow-y-auto p-4 bg-slate-50 dark:bg-slate-800/50">
              <OrderContextPanel order={order} ticket={ticket} />
            </div>
          )}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PLACE_ORDER MODAL
// Shows products, refund pills, storeSpend, storeUrl
// ═══════════════════════════════════════════════════════════════════════════
function PlaceOrderModal({ ticket, order, onClose, onComplete, onSaveData }) {
  const [storeUrl, setStoreUrl] = useState(order?.storeOrderConfirmationUrl || '');
  const [storeSpend, setStoreSpend] = useState(order?.storeSpend || '');
  const [refundedProducts, setRefundedProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  const products = order?.products || [];
  const isValidUrl = /^https?:\/\//i.test(storeUrl);
  const isValidSpend = parseFloat(storeSpend) > 0;
  const canComplete = isValidUrl && isValidSpend;

  const toggleRefund = (productIdx) => {
    setRefundedProducts(prev =>
      prev.includes(productIdx)
        ? prev.filter(i => i !== productIdx)
        : [...prev, productIdx]
    );
  };

  const handleComplete = async () => {
    if (!canComplete) return;
    setSaving(true);
    try {
      await onSaveData?.({
        storeOrderConfirmationUrl: storeUrl,
        storeSpend: parseFloat(storeSpend),
        refundedProducts,
      });
      await onComplete?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrapper
      title="Place Grocery Order"
      subtitle={`Order #${order?.uniqueId}`}
      order={order}
      ticket={ticket}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">
            Cancel
          </button>
          <button
            onClick={handleComplete}
            disabled={!canComplete || saving}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-colors ${
              canComplete && !saving
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/25'
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}
          >
            {saving ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiCheck className="w-4 h-4" />}
            Save & Complete
          </button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Products Section */}
        <Section title="Products to Place" icon={FiShoppingCart}>
          <div className="space-y-2">
            {products.length === 0 ? (
              <p className="text-sm text-slate-500">No products on this order.</p>
            ) : (
              products.map((p, idx) => {
                const isRefunded = refundedProducts.includes(idx) || p.refunded;
                return (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${
                      isRefunded
                        ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'
                        : 'bg-white border-slate-200 dark:bg-slate-800 dark:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-700 overflow-hidden flex items-center justify-center">
                        {p.image ? (
                          <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                        ) : (
                          <FiPackage className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <p className={`font-semibold ${isRefunded ? 'line-through text-slate-400' : 'text-slate-900 dark:text-white'}`}>
                          {p.name}
                        </p>
                        <p className="text-sm text-slate-500">Qty: {p.quantity || 1} × ${(p.price || 0).toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {p.substituteOrRefund && (
                        <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                          p.substituteOrRefund.toLowerCase().includes('refund')
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          Prefers {p.substituteOrRefund}
                        </span>
                      )}
                      <button
                        onClick={() => toggleRefund(idx)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                          isRefunded
                            ? 'bg-amber-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-amber-100 hover:text-amber-700 dark:bg-slate-700 dark:text-slate-300'
                        }`}
                      >
                        {isRefunded ? 'Refund Marked' : 'Mark Refund'}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          <button className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold transition-colors shadow-lg shadow-emerald-500/25">
            <FiShoppingCart className="w-4 h-4" />
            Add All to Cart
          </button>
        </Section>

        {/* Store Order Details */}
        <Section title="Order Confirmation" icon={FiLink}>
          <div className="space-y-4">
            <InputField
              label="Store Order URL"
              value={storeUrl}
              onChange={setStoreUrl}
              placeholder="https://store.com/order/confirmation/123"
              icon={FiExternalLink}
              error={storeUrl && !isValidUrl ? 'Must be a valid http/https URL' : null}
              required
            />
            <InputField
              label="Store Spend Total"
              value={storeSpend}
              onChange={setStoreSpend}
              placeholder="0.00"
              icon={FiDollarSign}
              type="number"
              step="0.01"
              error={storeSpend && !isValidSpend ? 'Must be greater than $0' : null}
              required
            />
          </div>
        </Section>

        {/* Validation Messages */}
        {(!isValidUrl || !isValidSpend) && (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
            <div className="flex items-start gap-3">
              <FiAlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-amber-800 dark:text-amber-200">Required fields missing</p>
                <ul className="mt-1 text-amber-700 dark:text-amber-300 list-disc list-inside">
                  {!isValidUrl && <li>Valid store order URL is required</li>}
                  {!isValidSpend && <li>Store spend must be greater than $0</li>}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// GET_DELIVERY_TIME MODAL
// Shows inStockDate + time in PST format, input for storeArrivalTime
// ═══════════════════════════════════════════════════════════════════════════
function GetDeliveryTimeModal({ ticket, order, onClose, onComplete, onSaveData }) {
  const [arrivalTime, setArrivalTime] = useState(order?.storeArrivalTime || '');
  const [saving, setSaving] = useState(false);

  const inStockDate = order?.inStockDate ? new Date(order.inStockDate) : null;
  const formattedDate = inStockDate?.toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: 'America/Los_Angeles'
  });

  const canComplete = arrivalTime.trim().length > 0;

  const handleComplete = async () => {
    if (!canComplete) return;
    setSaving(true);
    try {
      await onSaveData?.({ storeArrivalTime: arrivalTime });
      await onComplete?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrapper
      title="Set Delivery Time"
      subtitle={`Order #${order?.uniqueId}`}
      order={order}
      ticket={ticket}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button
            onClick={handleComplete}
            disabled={!canComplete || saving}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold ${canComplete && !saving ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}
          >
            {saving ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiCheck className="w-4 h-4" />}
            Save Time
          </button>
        </>
      }
    >
      <div className="space-y-6">
        <InfoCard
          icon={FiCalendar}
          label="In-Stock Date"
          value={formattedDate || 'Not provided'}
          highlight
        />

        <Section title="Set Store Arrival Time" icon={FiClock}>
          <p className="text-sm text-slate-500 mb-3">Enter the confirmed arrival window to show to the guest.</p>
          <InputField
            label="Store Arrival Time"
            value={arrivalTime}
            onChange={setArrivalTime}
            placeholder="e.g., 2:00 PM - 4:00 PM"
            icon={FiClock}
            required
          />
          <p className="text-xs text-slate-400 mt-2">
            <FiInfo className="w-3 h-3 inline mr-1" />
            This time will be shown to the guest and driver.
          </p>
        </Section>
      </div>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// VERIFY_ENTRY MODAL
// Shows property address, entry instructions, history with timestamps
// ═══════════════════════════════════════════════════════════════════════════
function VerifyEntryModal({ ticket, order, onClose, onComplete, onSaveData }) {
  const [entryInstructions, setEntryInstructions] = useState(order?.entryInstructions || '');
  const [saving, setSaving] = useState(false);

  const property = order?.property || order?.propertyID;
  const address = property?.address || {};
  const fullAddress = [address.line1, address.line2, address.city, address.state, address.zipCode].filter(Boolean).join(', ');
  const instructionsHistory = order?.entryInstructionsHistory || [];
  const resolvedEntryInstructions = (entryInstructions || order?.entryInstructions || '').trim();
  const entryInstructionsLastUpdatedLabel = formatEntryInstructionsLastUpdated(
    resolveEntryInstructionsLastUpdated(order)
  );

  const canComplete = entryInstructions.trim().length > 0;

  const handleComplete = async () => {
    if (!canComplete) return;
    setSaving(true);
    try {
      await onSaveData?.({ entryInstructions });
      await onComplete?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrapper
      title="Verify Entry Instructions"
      subtitle={`Order #${order?.uniqueId}`}
      order={order}
      ticket={ticket}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button
            onClick={handleComplete}
            disabled={!canComplete || saving}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold ${canComplete && !saving ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}
          >
            {saving ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiCheck className="w-4 h-4" />}
            Save Instructions
          </button>
        </>
      }
    >
      <div className="space-y-6">
        <InfoCard
          icon={FiHome}
          label="Property Address"
          value={fullAddress || 'No address on file'}
          highlight
        />

        <Section title="Entry Instructions" icon={FiClipboard}>
          {resolvedEntryInstructions && entryInstructionsLastUpdatedLabel && (
            <p className="mb-2 text-sm font-bold text-emerald-600">
              Last Updated: {entryInstructionsLastUpdatedLabel}
            </p>
          )}
          <textarea
            value={entryInstructions}
            onChange={e => setEntryInstructions(e.target.value)}
            placeholder="Gate code, parking notes, contact steps..."
            rows={5}
            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </Section>

        {/* Instructions History */}
        {instructionsHistory.length > 0 && (
          <Section title="Entry Instructions History" icon={FiClock}>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {instructionsHistory.map((entry, idx) => (
                <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <p className="text-sm text-slate-700 dark:text-slate-300">{entry.instructions}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {entry.updatedByName || 'System'} • {new Date(entry.updatedAt).toLocaleString()} • {entry.source}
                  </p>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SET_DRIVER_PAY MODAL (Admin Only)
// Shows order totals, tip, store spend, driver pay input
// ═══════════════════════════════════════════════════════════════════════════
function SetDriverPayModal({ ticket, order, onClose, onComplete, onSaveData }) {
  const [driverPay, setDriverPay] = useState(order?.driverPay || '');
  const [saving, setSaving] = useState(false);

  const orderTotal = (order?.groceryFee || 0) + (order?.hostFee || 0) + (order?.driverFee || 0) + (order?.tip || 0);
  const canComplete = parseFloat(driverPay) > 0;

  const handleComplete = async () => {
    if (!canComplete) return;
    setSaving(true);
    try {
      await onSaveData?.({ driverPay: parseFloat(driverPay) });
      await onComplete?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrapper
      title="Set Concierge Pay"
      subtitle={`Order #${order?.uniqueId}`}
      order={order}
      ticket={ticket}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button
            onClick={handleComplete}
            disabled={!canComplete || saving}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold ${canComplete && !saving ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}
          >
            {saving ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiCheck className="w-4 h-4" />}
            Save Driver Pay
          </button>
        </>
      }
    >
      <div className="space-y-6">
        <Section title="Order Totals" icon={FiDollarSign}>
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Order Total" value={`$${orderTotal.toFixed(2)}`} />
            <StatCard label="Tip" value={`$${(order?.tip || 0).toFixed(2)}`} highlight />
            <StatCard label="Store Spend" value={order?.storeSpend ? `$${order.storeSpend.toFixed(2)}` : 'Not set'} />
            <StatCard label="Grocery Fee" value={`$${(order?.groceryFee || 0).toFixed(2)}`} />
          </div>
        </Section>

        <Section title="Driver Payment" icon={FiTruck}>
          <InputField
            label="Driver Pay Amount"
            value={driverPay}
            onChange={setDriverPay}
            placeholder="0.00"
            icon={FiDollarSign}
            type="number"
            step="0.01"
            required
          />
        </Section>
      </div>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FIND_DRIVER MODAL
// Shows suggested driver list, slack send buttons, ping all, escalation
// ═══════════════════════════════════════════════════════════════════════════
function FindDriverModal({ ticket, order, onClose, onComplete, onSaveData, onEscalate }) {
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [saving, setSaving] = useState(false);

  const suggestedDrivers = order?.suggestedDrivers || [];
  const hasDriverAssigned = order?.driverID;

  if (hasDriverAssigned) {
    // Auto-resolve if driver assigned
    return (
      <ModalWrapper
        title="Find Concierge"
        subtitle={`Order #${order?.uniqueId}`}
        order={order}
        ticket={ticket}
        onClose={onClose}
        footer={
          <button
            onClick={onComplete}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <FiCheck className="w-4 h-4" />
            Driver Found - Resolve Ticket
          </button>
        }
      >
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiCheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Concierge Assigned!</h3>
          <p className="text-slate-500">This order now has a driver. You can resolve this ticket.</p>
        </div>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper
      title="Find Concierge"
      subtitle={`Order #${order?.uniqueId}`}
      order={order}
      ticket={ticket}
      onClose={onClose}
      footer={
        <>
          <button
            onClick={() => onEscalate?.(ticket)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-amber-600 hover:bg-amber-50"
          >
            <FiAlertTriangle className="w-4 h-4" />
            Escalate
          </button>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button>
        </>
      }
    >
      <div className="space-y-6">
        <Section title="Suggested Concierges" icon={FiUsers}>
          {suggestedDrivers.length === 0 ? (
            <div className="text-center py-8 bg-slate-50 dark:bg-slate-800 rounded-xl">
              <FiUsers className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-500">No suggested concierges available</p>
              <p className="text-xs text-slate-400 mt-1">Consider posting to external job board</p>
            </div>
          ) : (
            <div className="space-y-2">
              {suggestedDrivers.map((driver, idx) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-colors ${
                    selectedDriver === idx
                      ? 'bg-indigo-50 border-indigo-300'
                      : 'bg-white border-slate-200 hover:bg-slate-50'
                  }`}
                  onClick={() => setSelectedDriver(idx)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                      <FiUser className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{driver.driverName || 'Driver'}</p>
                      {driver.contacted && (
                        <p className="text-xs text-green-600">Contacted {driver.contactedAt ? new Date(driver.contactedAt).toLocaleTimeString() : ''}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!driver || !order) return;

                      try {
                        setSaving(true);
                        await request.post('/driver-chat/message', {
                          orderId: order._id || order.uniqueId,
                          driverId: driver.driverId || driver._id,
                          text: `Hi ${driver.driverName || driver.firstName}! We're interested in you for order #${order.uniqueId}. Please respond if you're available.`
                        });

                        // Update driver contacted status on success
                        const updatedSuggestedDrivers = [...suggestedDrivers];
                        updatedSuggestedDrivers[idx] = {
                          ...updatedSuggestedDrivers[idx],
                          contacted: true,
                          contactedAt: new Date().toISOString()
                        };
                        // You might want to update the order data here
                      } catch (error) {
                        console.error('Error sending Slack message:', error);
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving}
                    className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg text-xs font-semibold"
                  >
                    <FiSlack className="w-3 h-3" />
                    {saving ? 'Sending...' : 'Send'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        <div className="flex gap-3">
          <button className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-semibold">
            <FiSend className="w-4 h-4" />
            Ping All Drivers
          </button>
          <button
            onClick={() => onEscalate?.(ticket)}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold"
          >
            <FiAlertTriangle className="w-4 h-4" />
            Escalate
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MONITOR_DRIVER MODAL
// Shows Slack chat thread, Resolved button
// ═══════════════════════════════════════════════════════════════════════════
function MonitorDriverModal({ ticket, order, onClose, onComplete, onSaveData }) {
  const [resolved, setResolved] = useState(false);

  return (
    <ModalWrapper
      title="Monitor Concierge"
      subtitle={`Order #${order?.uniqueId}`}
      order={order}
      ticket={ticket}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button>
          <button
            onClick={onComplete}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <FiCheck className="w-4 h-4" />
            Resolved
          </button>
        </>
      }
    >
      <div className="space-y-6">
        <Section title="Driver Communication" icon={FiMessageSquare}>
          <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 min-h-[200px] border border-slate-200 dark:border-slate-700">
            {ticket.slackThreadUrl ? (
              <a
                href={ticket.slackThreadUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-semibold"
              >
                <FiSlack className="w-4 h-4" />
                Open Slack Thread
              </a>
            ) : (
              <p className="text-center text-slate-500">No Slack thread linked to this ticket</p>
            )}
          </div>
        </Section>

        <InfoCard
          icon={FiClock}
          label="Store Arrival Time"
          value={order?.storeArrivalTime || 'Not set'}
        />
      </div>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DRIVER_SUPPORT MODAL
// Message thread, reply input, issue resolved button
// ═══════════════════════════════════════════════════════════════════════════
function DriverSupportModal({ ticket, order, onClose, onComplete, onSaveData }) {
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef();

  // Fetch thread messages when modal opens
  useEffect(() => {
    const fetchThreadMessages = async () => {
      console.log('DriverSupportModal: Fetching messages for ticket:', ticket);
      console.log('Ticket orderId:', ticket.orderId);
      console.log('Order data:', order);

      try {
        // Get the order uniqueId - it might be in ticket.orderId or order.uniqueId
        let orderUniqueId = order?.uniqueId;
        if (!orderUniqueId && ticket.orderId) {
          // If orderId is a string, use it directly
          if (typeof ticket.orderId === 'string') {
            orderUniqueId = ticket.orderId;
          } else if (ticket.orderId && typeof ticket.orderId === 'object') {
            // If it's an object, it might have uniqueId
            orderUniqueId = ticket.orderId.uniqueId;
          }
        }

        console.log('Using orderUniqueId for matching:', orderUniqueId);

        // Find the OrderDriverThreads document to get the thread ID
        const threads = await request.get('/driver-chat/threads');

        console.log('Available threads:', threads);

        if (threads.success && threads.threads.length > 0) {
          // Find the matching thread for this order
          const matchingThread = threads.threads.find(t => {
            console.log('Comparing thread orderId:', t.orderId, 'with orderUniqueId:', orderUniqueId);
            return t.orderId === orderUniqueId;
          });

          console.log('Matching thread found:', matchingThread);

          if (matchingThread) {
            const messagesData = await request.get(`/driver-chat/threads/${matchingThread.id}/messages`);

            console.log('Messages data:', messagesData);

            if (messagesData.success) {
              setMessages(messagesData.messages || []);
            }
          } else {
            console.log('No matching thread found for orderId:', orderUniqueId);
            console.log('Available orderIds:', threads.threads.map(t => t.orderId));
          }
        } else {
          console.log('No threads available');
        }
      } catch (error) {
        console.error('Error fetching thread messages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchThreadMessages();
  }, [ticket]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendReply = async () => {
    if (!reply.trim() || !ticket) return;

    try {
      setSending(true);

      // Get the correct orderId and driverId for sending
      let orderId = order?.uniqueId || order?.orderId;
      let driverId = null;

      // Try to get driverId from the matching thread
      const threads = await request.get('/driver-chat/threads');

      if (threads.success && threads.threads.length > 0) {
        let orderUniqueId = order?.uniqueId;
        if (!orderUniqueId && ticket.orderId) {
          if (typeof ticket.orderId === 'string') {
            orderUniqueId = ticket.orderId;
          } else if (ticket.orderId && typeof ticket.orderId === 'object') {
            orderUniqueId = ticket.orderId.uniqueId;
          }
        }

        const matchingThread = threads.threads.find(t => t.orderId === orderUniqueId);
        if (matchingThread) {
          orderId = matchingThread.orderId;
          driverId = matchingThread.driverId;
        }
      }

      console.log('Sending reply:', {
        orderId: orderId,
        driverId: driverId,
        text: reply.trim()
      });

      if (!driverId) {
        console.error('No driverId found for sending reply');
        return;
      }

      await request.post('/driver-chat/message', {
        orderId: orderId,
        driverId: driverId,
        text: reply.trim()
      });

      console.log('Reply sent successfully');

      setReply('');
      // Refetch messages after sending
      await refetchMessages();

    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSending(false);
    }
  };

  const refetchMessages = async () => {
    try {
      let orderUniqueId = order?.uniqueId;
      if (!orderUniqueId && ticket.orderId) {
        if (typeof ticket.orderId === 'string') {
          orderUniqueId = ticket.orderId;
        } else if (ticket.orderId && typeof ticket.orderId === 'object') {
          orderUniqueId = ticket.orderId.uniqueId;
        }
      }

      const threads = await request.get('/driver-chat/threads');

      if (threads.success && threads.threads.length > 0) {
        const matchingThread = threads.threads.find(t => t.orderId === orderUniqueId);

        if (matchingThread) {
          const messagesData = await request.get(`/driver-chat/threads/${matchingThread.id}/messages`);

          if (messagesData.success) {
            setMessages(messagesData.messages || []);
          }
        }
      }
    } catch (error) {
      console.error('Error refetching messages:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  return (
    <ModalWrapper
      title="Driver Support Request"
      subtitle={`Order #${order?.uniqueId}`}
      order={order}
      ticket={ticket}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button>
          <button
            onClick={onComplete}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <FiCheck className="w-4 h-4" />
            Issue Resolved
          </button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Debug Info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs">
          <strong>Debug Info:</strong><br/>
          Ticket ID: {ticket?.id || ticket?._id}<br/>
          Order UniqueId: {order?.uniqueId}<br/>
          Ticket OrderId: {ticket?.orderId}<br/>
          Has Slack Data: {ticket?.slackChannelId ? 'Yes' : 'No'}
        </div>

        {/* Message Thread */}
        <Section title="Message Thread" icon={FiMessageSquare}>
          <div
            ref={scrollRef}
            className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 min-h-[300px] max-h-[400px] overflow-y-auto border border-slate-200 dark:border-slate-700"
          >
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <FiRefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                <span className="ml-2 text-slate-500">Loading messages...</span>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center text-slate-500">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <FiMessageSquare className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                  <p className="font-medium text-blue-800">Concierge Support Request</p>
                  <p className="text-sm text-blue-600 mt-1">
                    A driver has requested support for this order via Slack.
                  </p>
                  {ticket?.slackThreadUrl && (
                    <a
                      href={ticket.slackThreadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 mt-3 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg"
                    >
                      <FiSlack className="w-3 h-3" />
                      Open in Slack
                    </a>
                  )}
                </div>
                <p className="text-xs">Unable to load message thread. Concierge may have contacted support directly.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((message, index) => (
                  <div
                    key={message.id || index}
                    className={`flex ${message.isFromDriver ? 'justify-start' : 'justify-end'}`}
                  >
                    <div
                      className={`max-w-[70%] px-3 py-2 rounded-lg text-sm ${
                        message.isFromDriver
                          ? 'bg-white border border-slate-200 text-slate-800'
                          : 'bg-indigo-600 text-white'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{message.text}</p>
                      <p className={`text-xs mt-1 ${
                        message.isFromDriver ? 'text-slate-500' : 'text-indigo-200'
                      }`}>
                        {new Date(message.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Section>

        {/* Reply Input */}
        <div className="flex gap-2">
          <input
            value={reply}
            onChange={e => setReply(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your reply to the driver..."
            className="flex-1 px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            disabled={sending}
          />
          <button
            onClick={handleSendReply}
            disabled={sending || !reply.trim()}
            className="px-4 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-xl flex items-center"
          >
            {sending ? (
              <FiRefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <FiSend className="w-4 h-4" />
            )}
            {!sending && 'Send Reply'}
          </button>
        </div>

        {ticket?.slackThreadUrl && (
          <div className="text-center">
            <a
              href={ticket.slackThreadUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg"
            >
              <FiSlack className="w-4 h-4" />
              Open Full Conversation in Slack
            </a>
          </div>
        )}

        {ticket.slackThreadUrl && (
          <a
            href={ticket.slackThreadUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-semibold"
          >
            <FiSlack className="w-4 h-4" />
            Open in Slack
          </a>
        )}
      </div>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// REFUND_APPROVAL MODAL (Admin)
// Queue of refunds needing approval, order details, products with pricing
// ═══════════════════════════════════════════════════════════════════════════
function RefundApprovalModal({ ticket, order, onClose, onComplete, onSaveData }) {
  const [approvals, setApprovals] = useState({});

  const pendingRefunds = (order?.products || []).filter(p => p.refunded && p.refundApproved === null);

  const toggleApproval = (idx, approved) => {
    setApprovals(prev => ({ ...prev, [idx]: approved }));
  };

  const allDecided = pendingRefunds.every((_, idx) => approvals[idx] !== undefined);

  const handleComplete = async () => {
    await onSaveData?.({ refundApprovals: approvals });
    await onComplete?.();
  };

  return (
    <ModalWrapper
      title="Approve Refund Requests"
      subtitle={`Order #${order?.uniqueId}`}
      order={order}
      ticket={ticket}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button>
          <button
            onClick={handleComplete}
            disabled={!allDecided}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold ${allDecided ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}
          >
            <FiCheck className="w-4 h-4" />
            Submit Decisions
          </button>
        </>
      }
    >
      <div className="space-y-6">
        <Section title="Pending Refunds" icon={FiDollarSign}>
          {pendingRefunds.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No pending refunds</p>
          ) : (
            <div className="space-y-3">
              {pendingRefunds.map((product, idx) => (
                <div key={idx} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                        <FiPackage className="w-5 h-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-white">{product.name}</p>
                        <p className="text-sm text-slate-500">Qty: {product.quantity} × ${product.price?.toFixed(2)}</p>
                        <p className="text-xs text-amber-600 mt-1">Reason: {product.refundReason || 'Not specified'}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => toggleApproval(idx, true)}
                        className={`p-2 rounded-lg transition-colors ${
                          approvals[idx] === true
                            ? 'bg-emerald-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-600'
                        }`}
                      >
                        <FiCheckCircle className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => toggleApproval(idx, false)}
                        className={`p-2 rounded-lg transition-colors ${
                          approvals[idx] === false
                            ? 'bg-red-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-red-100 hover:text-red-600'
                        }`}
                      >
                        <FiXCircle className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Order Summary */}
        <Section title="Order Summary" icon={FiShoppingCart}>
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Store Spend" value={`$${(order?.storeSpend || 0).toFixed(2)}`} />
            <StatCard label="Total Items" value={order?.products?.length || 0} />
          </div>
        </Section>
      </div>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// POST_JOB_EXTERNAL MODAL
// TaskRabbit/GigSmart links, candidate fields, vetting
// ═══════════════════════════════════════════════════════════════════════════
function PostJobExternalModal({ ticket, order, onClose, onComplete, onSaveData, onEscalate }) {
  const [step, setStep] = useState(1); // 1: TaskRabbit, 2: GigSmart, 3: Candidate
  const [citiesSearched, setCitiesSearched] = useState('');
  const [candidateName, setCandidateName] = useState('');
  const [candidateContact, setCandidateContact] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [isVetted, setIsVetted] = useState(false);
  const [highRateReason, setHighRateReason] = useState('');
  const [saving, setSaving] = useState(false);

  const needsHighRateReason = parseFloat(hourlyRate) > 50;
  const canComplete = candidateName && candidateContact && hourlyRate && isVetted && (!needsHighRateReason || highRateReason);

  const handleComplete = async () => {
    if (!canComplete) return;
    setSaving(true);
    try {
      await onSaveData?.({
        externalJobData: {
          platform: step === 2 ? 'GigSmart' : 'TaskRabbit',
          candidateName,
          candidateContact,
          hourlyRate: parseFloat(hourlyRate),
          isVetted,
          citiesSearched: citiesSearched.split(',').map(c => c.trim()),
          vettingNotes: needsHighRateReason ? highRateReason : null,
        }
      });
      await onComplete?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrapper
      title="Post External Job"
      subtitle={`Order #${order?.uniqueId}`}
      order={order}
      ticket={ticket}
      onClose={onClose}
      footer={
        <>
          <button
            onClick={() => onEscalate?.(ticket)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-amber-600 hover:bg-amber-50"
          >
            <FiAlertTriangle className="w-4 h-4" />
            Escalate
          </button>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button
            onClick={handleComplete}
            disabled={!canComplete || saving}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold ${canComplete && !saving ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}
          >
            {saving ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiCheck className="w-4 h-4" />}
            Complete
          </button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Step 1: TaskRabbit */}
        {step === 1 && (
          <Section title="Step 1: Check TaskRabbit" icon={FiExternalLink}>
            <a
              href="https://www.taskrabbit.com"
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold"
            >
              <FiExternalLink className="w-4 h-4" />
              Open TaskRabbit
            </a>
            <button
              onClick={() => setStep(2)}
              className="w-full mt-3 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold"
            >
              Not Enough Candidates → Try GigSmart
            </button>
            <InputField
              label="Cities Searched"
              value={citiesSearched}
              onChange={setCitiesSearched}
              placeholder="Los Angeles, Santa Monica, Pasadena..."
            />
          </Section>
        )}

        {/* Step 2: GigSmart */}
        {step === 2 && (
          <Section title="Step 2: Check GigSmart" icon={FiExternalLink}>
            <a
              href="https://www.gigsmart.com"
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold"
            >
              <FiExternalLink className="w-4 h-4" />
              Open GigSmart
            </a>
            <button
              onClick={() => setStep(3)}
              className="w-full mt-3 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold"
            >
              Found Candidate → Enter Details
            </button>
          </Section>
        )}

        {/* Step 3: Candidate Details */}
        {step >= 3 && (
          <Section title="Candidate Details" icon={FiUser}>
            <div className="space-y-4">
              <InputField label="Driver Name" value={candidateName} onChange={setCandidateName} required />
              <InputField label="Contact (Phone/Email)" value={candidateContact} onChange={setCandidateContact} required />
              <InputField label="Hourly Rate" value={hourlyRate} onChange={setHourlyRate} type="number" icon={FiDollarSign} required />
              
              <label className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={isVetted}
                  onChange={e => setIsVetted(e.target.checked)}
                  className="rounded border-slate-300"
                />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Candidate Has Been Vetted
                </span>
              </label>

              {needsHighRateReason && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl">
                  <p className="text-sm font-semibold text-amber-800 mb-2">
                    <FiAlertTriangle className="w-4 h-4 inline mr-1" />
                    High Rate Justification Required
                  </p>
                  <p className="text-xs text-amber-700 mb-3">Rate above $50/hr requires explanation. This will auto-escalate.</p>
                  <textarea
                    value={highRateReason}
                    onChange={e => setHighRateReason(e.target.value)}
                    placeholder="Reason for higher rate..."
                    rows={3}
                    className="w-full px-3 py-2 bg-white border border-amber-300 rounded-lg text-sm"
                  />
                </div>
              )}
            </div>
          </Section>
        )}
      </div>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MONITOR_EXTERNAL_DRIVER MODAL
// External link, confirmation buttons, photo upload, completion
// ═══════════════════════════════════════════════════════════════════════════
function MonitorExternalDriverModal({ ticket, order, onClose, onComplete, onSaveData }) {
  const [step, setStep] = useState(1);
  const [photos, setPhotos] = useState([]);

  const externalLink = order?.externalJobLink || ticket?.externalJobData?.jobLink;

  return (
    <ModalWrapper
      title="Monitor External Driver"
      subtitle={`Order #${order?.uniqueId}`}
      order={order}
      ticket={ticket}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button>
          {step === 3 && (
            <button
              onClick={onComplete}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <FiCheck className="w-4 h-4" />
              Completed
            </button>
          )}
        </>
      }
    >
      <div className="space-y-6">
        {externalLink && (
          <a
            href={externalLink}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold"
          >
            <FiExternalLink className="w-4 h-4" />
            Open External Platform
          </a>
        )}

        <div className="space-y-3">
          <StepButton
            label="Confirmed External Bellhopt Concierge"
            completed={step >= 2}
            onClick={() => setStep(Math.max(step, 2))}
          />
          <StepButton
            label="External Bellhopt Concierge Picked Up Groceries"
            completed={step >= 3}
            onClick={() => setStep(Math.max(step, 3))}
            disabled={step < 2}
          />
        </div>

        {step >= 2 && (
          <Section title="Delivery Photos" icon={FiCamera}>
            <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-6 text-center">
              <FiImage className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Upload delivery photos here</p>
              <input type="file" multiple accept="image/*" className="mt-3" />
            </div>
          </Section>
        )}
      </div>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// INQUIRY_SUPPORT MODAL
// Shows inquiry, templates, thread, reply textarea
// ═══════════════════════════════════════════════════════════════════════════
function InquirySupportModal({ ticket, order, onClose, onComplete, onSaveData }) {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [reply, setReply] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);

  // Get inquiry data from ticket custom fields
  const inquiryData = ticket?.customFieldValues || {};
  const inquiryId = inquiryData.inquiryId;

  // Sample templates
  const templates = [
    { id: '1', name: 'Entry Instructions Request', content: 'Hi! We need the entry instructions for your upcoming delivery...' },
    { id: '2', name: 'Delivery Time Confirmation', content: 'Hello! We wanted to confirm your delivery time window...' },
    { id: '3', name: 'Order Update', content: 'Hi there! We have an update regarding your order...' },
  ];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template.id);
    setReply(template.content);
  };

  const handleSendEmail = async () => {
    if (!reply.trim() || !inquiryId) return;

    setSending(true);
    try {
      await request.post(`/inquiries/${inquiryId}/send-email`, {
        from: 'support@bellhopt.com',
        to: inquiryData.inquiryEmail,
        subject: 'Bellhopt Support Response',
        body: reply,
        userId: '507f1f77bcf86cd799439011', // TODO: Use actual host ID from context
      });
      setShowConfirm(false);
      setReply('');
      alert('Email sent successfully!');
      onComplete(); // Mark ticket as complete after sending
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalWrapper
      title="Contact Host"
      subtitle={`Order #${order?.uniqueId}`}
      order={order}
      ticket={ticket}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button>
          <button
            onClick={onComplete}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <FiCheck className="w-4 h-4" />
            Mark Complete
          </button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Templates */}
        <Section title="Templates" icon={FiClipboard}>
          <div className="flex flex-wrap gap-2">
            {templates.map(template => (
              <button
                key={template.id}
                onClick={() => handleTemplateSelect(template)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedTemplate === template.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {template.name}
              </button>
            ))}
          </div>
        </Section>

        {/* Inquiry Details */}
        <Section title="Inquiry Details" icon={FiMessageSquare}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-slate-700 dark:text-slate-300">Name</p>
                <p className="text-slate-600 dark:text-slate-400">{inquiryData.inquiryName || 'N/A'}</p>
              </div>
              <div>
                <p className="font-medium text-slate-700 dark:text-slate-300">Email</p>
                <p className="text-slate-600 dark:text-slate-400">{inquiryData.inquiryEmail || 'N/A'}</p>
              </div>
              <div>
                <p className="font-medium text-slate-700 dark:text-slate-300">Phone</p>
                <p className="text-slate-600 dark:text-slate-400">{inquiryData.inquiryPhone || 'N/A'}</p>
              </div>
              <div>
                <p className="font-medium text-slate-700 dark:text-slate-300">Type</p>
                <p className="text-slate-600 dark:text-slate-400">{inquiryData.inquiryType || 'N/A'}</p>
              </div>
            </div>
            <div>
              <p className="font-medium text-slate-700 dark:text-slate-300 mb-2">Message</p>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{inquiryData.inquiryMessage || 'No message content'}</p>
              </div>
            </div>
          </div>
        </Section>

        {/* Reply */}
        <Section title="Your Reply" icon={FiEdit3}>
          <textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            placeholder="Type your message..."
            rows={4}
            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {reply && !showConfirm && (
            <button
              onClick={() => setShowConfirm(true)}
              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold"
            >
              <FiSend className="w-4 h-4" />
              Review & Send
            </button>
          )}
          {showConfirm && (
            <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm font-semibold text-amber-800 mb-3">Are you sure you want to send this message?</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="flex-1 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium"
                >
                  Edit
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={sending}
                  className="flex-1 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <>
                      <FiRefreshCw className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FiSend className="w-4 h-4" />
                      Confirm Send
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </Section>
      </div>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// RESCHEDULE_ORDER MODAL
// Current dates, new date input, accuracy checkbox, admin approval
// ═══════════════════════════════════════════════════════════════════════════
function RescheduleOrderModal({ ticket, order, onClose, onComplete, onSaveData }) {
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [isAccurate, setIsAccurate] = useState(false);
  const [saving, setSaving] = useState(false);

  const canComplete = newDate && newTime && isAccurate;

  const handleComplete = async () => {
    if (!canComplete) return;
    setSaving(true);
    try {
      await onSaveData?.({
        rescheduleData: {
          originalDate: order?.inStockDate,
          newDate,
          newTime,
          guestConfirmed: isAccurate,
        }
      });
      // Note: This sends to admin for approval, doesn't complete yet
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrapper
      title="Reschedule Order"
      subtitle={`Order #${order?.uniqueId}`}
      order={order}
      ticket={ticket}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button
            onClick={handleComplete}
            disabled={!canComplete || saving}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold ${canComplete && !saving ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}
          >
            {saving ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
            Send for Admin Approval
          </button>
        </>
      }
    >
      <div className="space-y-6">
        <Section title="Current Schedule" icon={FiCalendar}>
          <div className="grid grid-cols-2 gap-3">
            <InfoCard
              icon={FiCalendar}
              label="Current In-Stock Date"
              value={order?.inStockDate ? new Date(order.inStockDate).toLocaleDateString() : 'Not set'}
            />
            <InfoCard
              icon={FiClock}
              label="Current Arrival Time"
              value={order?.storeArrivalTime || 'Not set'}
            />
          </div>
        </Section>

        <Section title="New Schedule" icon={FiEdit3}>
          <div className="grid grid-cols-2 gap-4">
            <InputField
              label="New Delivery Date"
              value={newDate}
              onChange={setNewDate}
              type="date"
              required
            />
            <InputField
              label="New Time Window"
              value={newTime}
              onChange={setNewTime}
              placeholder="e.g., 2-4pm"
              required
            />
          </div>
        </Section>

        <label className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl cursor-pointer">
          <input
            type="checkbox"
            checked={isAccurate}
            onChange={e => setIsAccurate(e.target.checked)}
            className="mt-0.5 rounded border-amber-300"
          />
          <div>
            <span className="text-sm font-semibold text-amber-800 dark:text-amber-200">
              I confirm this information is accurate
            </span>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              This must be checked before submitting. Inaccurate schedules may affect delivery.
            </p>
          </div>
        </label>

        {!isAccurate && newDate && newTime && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <FiAlertCircle className="w-4 h-4" />
            <span>Please confirm the accuracy checkbox to proceed</span>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CANCEL_ORDER MODAL
// Order details, cancel button, reason textbox
// ═══════════════════════════════════════════════════════════════════════════
function CancelOrderModal({ ticket, order, onClose, onComplete, onSaveData }) {
  const [reason, setReason] = useState('');
  const [proofLink, setProofLink] = useState('');
  const [saving, setSaving] = useState(false);

  const canComplete = reason.trim().length > 10;

  const handleComplete = async () => {
    if (!canComplete) return;
    setSaving(true);
    try {
      await onSaveData?.({
        cancellationReason: reason,
        cancellationProofLink: proofLink,
      });
      // This would need manager approval
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrapper
      title="Cancel Order"
      subtitle={`Order #${order?.uniqueId}`}
      order={order}
      ticket={ticket}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button
            onClick={handleComplete}
            disabled={!canComplete || saving}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold ${canComplete && !saving ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}
          >
            {saving ? <FiRefreshCw className="w-4 h-4 animate-spin" /> : <FiX className="w-4 h-4" />}
            Request Cancellation
          </button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <div className="flex items-start gap-3">
            <FiAlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800 dark:text-red-200">Manager Approval Required</p>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                This action requires manager approval before the order is cancelled.
              </p>
            </div>
          </div>
        </div>

        <Section title="Order Summary" icon={FiShoppingCart}>
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Order Total" value={`$${((order?.groceryFee || 0) + (order?.tip || 0)).toFixed(2)}`} />
            <StatCard label="Items" value={order?.products?.length || 0} />
          </div>
        </Section>

        <Section title="Cancellation Details" icon={FiEdit3}>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Reason for cancellation (required)..."
            rows={4}
            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          {reason.length > 0 && reason.length < 10 && (
            <p className="text-xs text-red-600 mt-1">Please provide more detail (minimum 10 characters)</p>
          )}

          <InputField
            label="Link to Email Proof (optional)"
            value={proofLink}
            onChange={setProofLink}
            placeholder="https://..."
            icon={FiLink}
          />
        </Section>
      </div>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// REMAINING MODAL STUBS (implement as needed)
// ═══════════════════════════════════════════════════════════════════════════

function SubstitutionReviewModal({ ticket, order, onClose, onComplete }) {
  const products = order?.products || [];

  return (
    <ModalWrapper title="Review Substitution" subtitle={`Order #${order?.uniqueId}`} order={order} ticket={ticket} onClose={onClose}
      footer={<><button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button><button onClick={onComplete} className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"><FiCheck className="w-4 h-4" />Complete</button></>}>
      <Section title="Products" icon={FiPackage}>
        {products.map((p, idx) => (
          <div key={idx} className="p-3 bg-slate-50 rounded-xl flex items-center justify-between mb-2">
            <span className="font-medium">{p.name}</span>
            <span className="text-sm text-slate-500">{p.substituteOrRefund || 'No preference'}</span>
          </div>
        ))}
      </Section>
    </ModalWrapper>
  );
}

function QualityVerificationModal({ ticket, order, onClose, onComplete }) {
  const photos = order?.qualityVerification?.photoUrls || [];

  return (
    <ModalWrapper title="Quality Verification" subtitle={`Order #${order?.uniqueId}`} order={order} ticket={ticket} onClose={onClose}
      footer={<><button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button><button onClick={onComplete} className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"><FiCheck className="w-4 h-4" />Approve</button></>}>
      <Section title="Uploaded Photos" icon={FiCamera}>
        {photos.length === 0 ? (
          <p className="text-center text-slate-500 py-8">No photos uploaded yet</p>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {photos.map((url, idx) => (
              <img key={idx} src={url} alt={`Photo ${idx + 1}`} className="rounded-xl object-cover aspect-square" />
            ))}
          </div>
        )}
      </Section>
    </ModalWrapper>
  );
}

function ReviewDeliveryItemsModal({ ticket, order, onClose, onComplete }) {
  return (
    <ModalWrapper title="Review Delivery Items" subtitle={`Order #${order?.uniqueId}`} order={order} ticket={ticket} onClose={onClose}
      footer={<><button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button><button onClick={onComplete} className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"><FiCheck className="w-4 h-4" />Complete Review</button></>}>
      <Section title="Products to Verify" icon={FiPackage}>
        {(order?.products || []).map((p, idx) => (
          <div key={idx} className="p-3 bg-slate-50 rounded-xl flex items-center justify-between mb-2">
            <div>
              <p className="font-medium">{p.name}</p>
              <p className="text-sm text-slate-500">Qty: {p.quantity}</p>
            </div>
            <div className="flex gap-2">
              <button className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold">Substitute</button>
              <button className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-xs font-semibold">Refund</button>
            </div>
          </div>
        ))}
      </Section>
    </ModalWrapper>
  );
}

function VendorRefundModal({ ticket, order, onClose, onComplete, onSaveData }) {
  const [processed, setProcessed] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [noRefundReason, setNoRefundReason] = useState('');

  const canComplete = processed !== null && (processed === false || refundAmount);

  return (
    <ModalWrapper title="Vendor Refund" subtitle={`Order #${order?.uniqueId}`} order={order} ticket={ticket} onClose={onClose}
      footer={<><button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button><button onClick={onComplete} disabled={!canComplete} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold ${canComplete ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}><FiCheck className="w-4 h-4" />Complete</button></>}>
      <Section title="Store Information" icon={FiExternalLink}>
        {order?.storeOrderConfirmationUrl && (
          <a href={order.storeOrderConfirmationUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold">
            <FiExternalLink className="w-4 h-4" />Open Store Order
          </a>
        )}
      </Section>

      <Section title="Refund Status" icon={FiDollarSign}>
        <p className="text-sm text-slate-600 mb-3">Was the refund processed by the vendor?</p>
        <div className="flex gap-3">
          <button onClick={() => setProcessed(true)} className={`flex-1 px-4 py-3 rounded-xl font-semibold ${processed === true ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700'}`}>Yes</button>
          <button onClick={() => setProcessed(false)} className={`flex-1 px-4 py-3 rounded-xl font-semibold ${processed === false ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-700'}`}>No</button>
        </div>
        {processed === true && <InputField label="Refunded Amount" value={refundAmount} onChange={setRefundAmount} type="number" icon={FiDollarSign} className="mt-4" />}
        {processed === false && <textarea value={noRefundReason} onChange={e => setNoRefundReason(e.target.value)} placeholder="Reason refund was not processed..." rows={3} className="w-full mt-4 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm" />}
      </Section>
    </ModalWrapper>
  );
}

function GuestSupportModal({ ticket, order, onClose, onComplete }) {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  // Get inquiry data from ticket custom fields
  const inquiryData = ticket?.customFieldValues || {};
  const inquiryId = inquiryData.inquiryId;

  const handleSendEmail = async () => {
    if (!reply.trim() || !inquiryId) return;

    setSending(true);
    try {
      await request.post(`/inquiries/${inquiryId}/send-email`, {
        from: 'support@bellhopt.com',
        to: inquiryData.inquiryEmail,
        subject: 'Bellhopt Support Response',
        body: reply,
        userId: '507f1f77bcf86cd799439011', // TODO: Use actual host ID from context
      });
      setReply('');
      alert('Email sent successfully!');
      onComplete(); // Mark ticket as complete after sending
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalWrapper title="Guest Support" subtitle={`Order #${order?.uniqueId}`} order={order} ticket={ticket} onClose={onClose}
      footer={<><button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button><button onClick={onComplete} className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"><FiCheck className="w-4 h-4" />Resolved</button></>}>
      <div className="space-y-6">
        {/* Inquiry Details */}
        <Section title="Guest Inquiry" icon={FiMessageSquare}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-slate-700 dark:text-slate-300">Name</p>
                <p className="text-slate-600 dark:text-slate-400">{inquiryData.inquiryName || 'N/A'}</p>
              </div>
              <div>
                <p className="font-medium text-slate-700 dark:text-slate-300">Email</p>
                <p className="text-slate-600 dark:text-slate-400">{inquiryData.inquiryEmail || 'N/A'}</p>
              </div>
              <div>
                <p className="font-medium text-slate-700 dark:text-slate-300">Phone</p>
                <p className="text-slate-600 dark:text-slate-400">{inquiryData.inquiryPhone || 'N/A'}</p>
              </div>
              <div>
                <p className="font-medium text-slate-700 dark:text-slate-300">Type</p>
                <p className="text-slate-600 dark:text-slate-400">{inquiryData.inquiryType || 'N/A'}</p>
              </div>
            </div>
            <div>
              <p className="font-medium text-slate-700 dark:text-slate-300 mb-2">Message</p>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{inquiryData.inquiryMessage || 'No message content'}</p>
              </div>
            </div>
          </div>
        </Section>

        {/* Reply */}
        <Section title="Send Reply" icon={FiEdit3}>
          <textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            placeholder="Type your response to the guest..."
            rows={4}
            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {reply && (
            <button
              onClick={handleSendEmail}
              disabled={sending}
              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-xl text-sm font-semibold"
            >
              {sending ? (
                <>
                  <FiRefreshCw className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <FiSend className="w-4 h-4" />
                  Send Email
                </>
              )}
            </button>
          )}
        </Section>
      </div>
    </ModalWrapper>
  );
}

function GenericTicketModal({ ticket, order, onClose, onComplete }) {
  return (
    <ModalWrapper title={ticket?.name || 'Ticket'} subtitle={`Order #${order?.uniqueId}`} order={order} ticket={ticket} onClose={onClose}
      footer={<><button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button><button onClick={onComplete} className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"><FiCheck className="w-4 h-4" />Complete</button></>}>
      <p className="text-center text-slate-500 py-8">Work this ticket from the main interface</p>
    </ModalWrapper>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════
function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function InputField({ label, value, onChange, placeholder, icon: Icon, type = 'text', error, required, ...props }) {
  return (
    <div>
      {label && (
        <label className="block text-xs font-medium text-slate-500 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />}
        <input
          type={type}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 border ${error ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'} rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
          {...props}
        />
      </div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`p-4 rounded-xl border ${highlight ? 'bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-800' : 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700'}`}>
      <div className="flex items-start gap-3">
        {Icon && <Icon className={`w-5 h-5 mt-0.5 ${highlight ? 'text-indigo-600' : 'text-slate-400'}`} />}
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className={`text-sm font-semibold mt-0.5 ${highlight ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-900 dark:text-white'}`}>{value}</p>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`p-3 rounded-xl text-center ${highlight ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'bg-slate-50 dark:bg-slate-800'}`}>
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-lg font-bold mt-0.5 ${highlight ? 'text-indigo-600' : 'text-slate-900 dark:text-white'}`}>{value}</p>
    </div>
  );
}

function StepButton({ label, completed, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center justify-between p-4 rounded-xl border transition-colors ${
        completed
          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
          : disabled
          ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
      }`}
    >
      <span className="font-medium">{label}</span>
      {completed && <FiCheckCircle className="w-5 h-5" />}
    </button>
  );
}

